import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const ContactContainer = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #000000 100%);
  padding: 4rem 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const ContactContent = styled.div`
  max-width: 1200px;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled.div`
  color: #ffffff;
`;

const ContactTitle = styled(motion.h2)`
  font-family: 'Orbitron', monospace;
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 900;
  color: #00ffff;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  margin-bottom: 2rem;
`;

const ContactSubtitle = styled(motion.p)`
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.1rem, 2.5vw, 1.5rem);
  color: #cccccc;
  margin-bottom: 3rem;
  line-height: 1.6;
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ContactButton = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem 2rem;
  margin-bottom: 1.5rem;
  background: rgba(0, 17, 34, 0.95);
  border: 2px solid #00ffff;
  border-radius: 8px;
  color: #00ffff;
  font-family: 'Orbitron', monospace;
  font-size: 1.1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  text-decoration: none;
  box-shadow: 0 0 16px 0 rgba(0,255,255,0.08);
  transition: all 0.2s cubic-bezier(.4,0,.2,1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  z-index: 1;
  &:hover {
    background: #00ffff;
    color: #000;
    box-shadow: 0 0 24px 0 #00ffff;
    svg {
      filter: drop-shadow(0 0 8px #00ffff);
    }
    img {
      filter: drop-shadow(0 0 8px #00ffff);
    }
  }
  @media (max-width: 600px) {
    font-size: 1rem;
    padding: 1rem 1.2rem;
  }
`;

const ContactButtonIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 28px;
    height: 28px;
    display: block;
    fill: currentColor;
    color: #00ffff;
    transition: filter 0.2s;
  }
  img {
    width: 28px;
    height: 28px;
    display: block;
    transition: filter 0.2s;
  }
`;

const ContactForm = styled(motion.form)`
  background: rgba(0, 17, 34, 0.9);
  border: 2px solid rgba(0, 255, 255, 0.5);
  border-radius: 12px;
  padding: 3rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #00ffff, transparent);
    animation: scan 3s linear infinite;
  }
  
  @keyframes scan {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
`;

const FormLabel = styled.label`
  font-family: 'Orbitron', monospace;
  font-size: 0.9rem;
  color: #00ffff;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.5rem;
  display: block;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 4px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #00ffff;
    box-shadow: 0 0 10px rgba(0, 255, 255, 0.3);
  }
  
  &::placeholder {
    color: #666666;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 4px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #00ffff;
    box-shadow: 0 0 10px rgba(0, 255, 255, 0.3);
  }
  
  &::placeholder {
    color: #666666;
  }
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(45deg, #00ffff, #0080ff);
  border: none;
  border-radius: 4px;
  color: #000000;
  font-family: 'Orbitron', monospace;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 255, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled(motion.div)`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  text-align: center;
  background: ${props => props.success ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)'};
  border: 1px solid ${props => props.success ? '#00ff00' : '#ff0000'};
  color: ${props => props.success ? '#00ff00' : '#ff0000'};
`;

const ContactFormComponent = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [status, setStatus] = useState({ show: false, success: false, message: '' });

  useEffect(() => {
    const handler = (e) => {
      setFormData((prev) => ({ ...prev, message: e.detail.message }));
    };
    window.addEventListener('autofill-contact-message', handler);
    return () => window.removeEventListener('autofill-contact-message', handler);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setStatus({
      show: true,
      success: false,
      message: 'Sending message...'
    });

    try {
      const response = await fetch('https://formspree.io/f/mpwrgnpr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: formData.message,
          subject: `Contact from ${formData.name} - ${formData.company}`
        })
      });

      if (response.ok) {
        setStatus({
          show: true,
          success: true,
          message: 'Message sent successfully! We\'ll get back to you soon.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setStatus({
        show: true,
        success: false,
        message: 'Failed to send message. Please try again or contact us directly.'
      });
    }
    
    // Hide status after 5 seconds
    setTimeout(() => {
      setStatus({ show: false, success: false, message: '' });
    }, 5000);
  };

  const contactButtons = [
    {
      label: 'Contact via LinkedIn',
      link: 'https://www.linkedin.com/company/watch-dogs-cyber-specialists/',
      icon: <img src="/linkedin-icon2.png" alt="LinkedIn" style={{ width: '28px', height: '28px' }} />
    },
    {
      label: 'Contact via WhatsApp',
      link: 'https://wa.me/27815824228',
      icon: <img src="/whatsapp-icon2.png" alt="WhatsApp" style={{ width: '28px', height: '28px' }} />
    },
    {
      label: 'Connect on Instagram',
      link: 'https://www.instagram.com/watchdogs_cyber_za/',
      icon: <img src="/instagram-icon.png" alt="Instagram" style={{ width: '28px', height: '28px' }} />
    }
  ];

  return (
    <ContactContainer>
      <ContactContent>
        <ContactInfo>
          <ContactTitle
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            Contact Us
          </ContactTitle>
          
          <ContactSubtitle
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Ready to secure your digital frontier? Get in touch with our elite cybersecurity team.
          </ContactSubtitle>

          <ContactDetails>
            {contactButtons.map((btn, idx) => (
              <ContactButton
                key={btn.label}
                href={btn.link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={btn.label}
              >
                <ContactButtonIcon>{btn.icon}</ContactButtonIcon>
                {btn.label}
              </ContactButton>
            ))}
          </ContactDetails>
        </ContactInfo>

        <ContactForm
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
        >
          <FormGroup>
            <FormLabel>Name *</FormLabel>
            <FormInput
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Your full name"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Email *</FormLabel>
            <FormInput
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.email@company.com"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Company</FormLabel>
            <FormInput
              type="text"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              placeholder="Your company name"
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Message *</FormLabel>
            <FormTextarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Tell us about your cybersecurity needs..."
              required
            />
          </FormGroup>

          <SubmitButton
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Send Message
          </SubmitButton>

          {status.show && (
            <StatusMessage
              success={status.success}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {status.message}
            </StatusMessage>
          )}
        </ContactForm>
      </ContactContent>
    </ContactContainer>
  );
};

export default ContactFormComponent; 